import React, { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HelpCircle, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useRequestPasswordReset } from '@/hooks/auth/use-request-password-reset';

type FormErrors = {
  email?: string;
};

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { requestReset, isLoading } = useRequestPasswordReset({
    onSuccess: () => {
      setIsSubmitted(true);
    },
    onError: () => {
      // Still show success to avoid leaking whether email exists
      setIsSubmitted(true);
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (value: string) => {
    setEmail(value);
    if (errors.email) {
      setErrors({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    await requestReset({ email: email.trim() });
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col items-center pt-24 p-4 bg-white dark:bg-slate-900">
        <div className="w-full max-w-lg space-y-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              Check your email
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              If an account exists for <strong>{email}</strong>, we've sent a
              password reset link. The link will expire in 15 minutes.
            </p>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Didn't receive the email? Check your spam folder or{' '}
            <span
              onClick={() => setIsSubmitted(false)}
              className="text-primary cursor-pointer hover:underline"
            >
              try again
            </span>
            .
          </p>
          <div className="pt-4">
            <Link to="/signin">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex flex-col items-center pt-24 p-4 bg-white dark:bg-slate-900">
      <div className="w-full max-w-lg space-y-8">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              Forgot your password?
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => handleInputChange(e.target.value)}
                className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                autoComplete="email"
                autoFocus
              />
            </div>
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={!email.trim() || isLoading}
            className="w-full"
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <div className="text-center">
          <Link
            to="/signin"
            className="inline-flex items-center text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
